import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Plus, Trash2, GripVertical, Save, Loader2 } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getFormAdmin, upsertForm } from "@/lib/forms.functions";
import { FormRenderer } from "@/components/form-renderer";

const q = (id: string) => queryOptions({ queryKey: ["admin", "form", id], queryFn: () => getFormAdmin({ data: { id } }) });

export const Route = createFileRoute("/_authenticated/admin/forms/$id")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(q(params.id)),
  errorComponent: ({ error }) => <p className="p-6 text-muted-foreground">{String(error)}</p>,
  notFoundComponent: () => <p className="p-6">غير موجود</p>,
  component: FormEditor,
});

const TYPES = [
  { v: "short_text", l: "نص قصير" },
  { v: "long_text", l: "نص طويل" },
  { v: "single_choice", l: "اختيار واحد" },
  { v: "multi_choice", l: "اختيار متعدد" },
  { v: "scale", l: "مقياس رقمي" },
  { v: "date", l: "تاريخ" },
  { v: "email", l: "بريد إلكتروني" },
  { v: "phone", l: "هاتف" },
  { v: "number", l: "رقم" },
];

function FormEditor() {
  const id = Route.useParams().id;
  const { data: form } = useSuspenseQuery(q(id));
  const save = useServerFn(upsertForm);
  const qc = useQueryClient();

  const hasSections = Array.isArray((form as any)?.schema?.sections) && (form as any).schema.sections.length > 0;

  if (hasSections) {
    return (
      <div>
        <div className="mb-4 flex items-center justify-between">
          <Link to="/admin/forms" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowRight className="h-4 w-4" /> عودة إلى النماذج
          </Link>
        </div>
        <div className="rounded-ds-lg bg-card p-8 text-center shadow-soft">
          <h1 className="font-display text-2xl font-extrabold text-foreground">{form?.title_ar}</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            هذا النموذج مُدار بواسطة المحتوى — لا يمكن تعديله من هنا. للتعديل يُرجى التواصل مع المطوّر.
          </p>
          <p className="mt-2 text-xs text-muted-foreground/70">
            عدد الأقسام: {(form as any).schema.sections.length}
          </p>
        </div>
      </div>
    );
  }

  const [meta, setMeta] = useState({
    title_ar: form?.title_ar ?? "",
    slug: form?.slug ?? "",
    description_ar: form?.description_ar ?? "",
    audience: (form?.audience ?? "client") as "client" | "staff" | "public",
    category: form?.category ?? "",
    is_active: !!form?.is_active,
  });
  const [fields, setFields] = useState<any[]>(((form as any)?.schema?.fields as any[]) ?? []);
  const [selectedId, setSelectedId] = useState<string | null>(fields[0]?.id ?? null);
  const [busy, setBusy] = useState(false);

  const selected = fields.find((f) => f.id === selectedId) ?? null;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    if (!selectedId && fields.length) setSelectedId(fields[0].id);
  }, [fields, selectedId]);

  function addField(type: string) {
    const newField = {
      id: `q${Date.now().toString(36)}`,
      type,
      label_ar: "سؤال جديد",
      required: false,
      ...(type === "single_choice" || type === "multi_choice" ? { options: ["خيار 1", "خيار 2"] } : {}),
      ...(type === "scale" ? { min: 1, max: 5 } : {}),
    };
    setFields([...fields, newField]);
    setSelectedId(newField.id);
  }
  function updateField(patch: any) {
    if (!selected) return;
    setFields(fields.map((f) => (f.id === selected.id ? { ...f, ...patch } : f)));
  }
  function removeField(fid: string) {
    setFields(fields.filter((f) => f.id !== fid));
    if (selectedId === fid) setSelectedId(null);
  }
  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = fields.map((f) => f.id);
    const oldIdx = ids.indexOf(String(active.id));
    const newIdx = ids.indexOf(String(over.id));
    const next = [...fields];
    const [item] = next.splice(oldIdx, 1);
    next.splice(newIdx, 0, item);
    setFields(next);
  }

  async function handleSave() {
    setBusy(true);
    try {
      await save({
        data: {
          id,
          title_ar: meta.title_ar,
          slug: meta.slug,
          description_ar: meta.description_ar,
          audience: meta.audience,
          category: meta.category || null,
          schema: { fields },
          is_active: meta.is_active,
        },
      });
      toast.success("تم الحفظ");
      qc.invalidateQueries({ queryKey: ["admin", "form", id] });
      qc.invalidateQueries({ queryKey: ["admin", "forms"] });
    } catch (e: any) { toast.error(e?.message ?? "فشل الحفظ"); } finally { setBusy(false); }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <Link to="/admin/forms" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowRight className="h-4 w-4" /> عودة إلى النماذج
        </Link>
        <button
          onClick={handleSave}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 font-display text-sm font-bold text-primary-foreground shadow-soft hover:bg-primary-hover disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} حفظ
        </button>
      </div>

      <div className="mb-6 rounded-ds-lg bg-card p-6 shadow-soft">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="العنوان" value={meta.title_ar} onChange={(v) => setMeta({ ...meta, title_ar: v })} />
          <Field label="Slug" value={meta.slug} onChange={(v) => setMeta({ ...meta, slug: v })} mono />
          <Field label="الفئة" value={meta.category} onChange={(v) => setMeta({ ...meta, category: v })} />
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">الجمهور</span>
            <select value={meta.audience} onChange={(e) => setMeta({ ...meta, audience: e.target.value as any })} className="w-full rounded-xl border border-input bg-background px-3 py-2">
              <option value="client">العملاء (مسجّلن)</option>
              <option value="staff">الفريق</option>
              <option value="public">عام (بدون تسجيل)</option>
            </select>
          </label>
        </div>
        <div className="mt-4">
          <Field label="الوصف" value={meta.description_ar} onChange={(v) => setMeta({ ...meta, description_ar: v })} textarea rows={3} />
        </div>
        <label className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-foreground">
          <input type="checkbox" checked={meta.is_active} onChange={(e) => setMeta({ ...meta, is_active: e.target.checked })} className="h-4 w-4" />
          تفعيل النموذج (يظهر للجمهور المستهدف)
        </label>
      </div>

      <div className="grid gap-6 lg:grid-cols-[280px_1fr_360px]">
        <div className="rounded-ds-lg bg-card p-4 shadow-soft">
          <p className="mb-3 font-display text-sm font-bold text-foreground">أضف حقلاً</p>
          <div className="space-y-1">
            {TYPES.map((t) => (
              <button
                key={t.v}
                onClick={() => addField(t.v)}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start text-sm text-muted-foreground hover:bg-primary/10 hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" /> {t.l}
              </button>
            ))}
          </div>
          <div className="mt-6 border-t border-border/60 pt-4">
            <p className="mb-2 font-display text-sm font-bold text-foreground">ترتيب الحقول</p>
            {fields.length === 0 ? (
              <p className="text-xs text-muted-foreground">لا حقول بعد.</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                  <ul className="space-y-1">
                    {fields.map((f) => (
                      <SortableFieldItem key={f.id} f={f} selected={selectedId === f.id} onSelect={() => setSelectedId(f.id)} onDelete={() => removeField(f.id)} />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>

        <div className="rounded-ds-lg bg-card p-6 shadow-soft">
          <p className="mb-4 font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">المعاينة</p>
          <div className="rounded-2xl border border-border/60 bg-background/60 p-5">
            <FormRenderer schema={{ fields }} answers={{}} onChange={() => {}} disabled />
            {fields.length === 0 && <p className="text-center text-sm text-muted-foreground">أضف حقولاً لتظهر هنا.</p>}
          </div>
        </div>

        <div className="rounded-ds-lg bg-card p-5 shadow-soft">
          <p className="mb-4 font-display text-sm font-bold text-foreground">إعدادات الحقل</p>
          {!selected ? (
            <p className="text-sm text-muted-foreground">اختر حقلاً لتحرير خصائصه.</p>
          ) : (
            <div className="space-y-4">
              <Field label="عنوان الحقل" value={selected.label_ar} onChange={(v) => updateField({ label_ar: v })} />
              <Field label="نص مساعد" value={selected.help_ar ?? ""} onChange={(v) => updateField({ help_ar: v })} />
              <Field label="المعرّف" value={selected.id} onChange={(v) => updateField({ id: v })} mono />
              <label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <input type="checkbox" checked={!!selected.required} onChange={(e) => updateField({ required: e.target.checked })} className="h-4 w-4" />
                إلزامي
              </label>
              {(selected.type === "single_choice" || selected.type === "multi_choice") && (
                <Field
                  label="الخيارات (سطر لكل خيار)"
                  value={(selected.options ?? []).join("\n")}
                  onChange={(v) => updateField({ options: v.split("\n").map((s: string) => s.trim()).filter(Boolean) })}
                  textarea
                  rows={5}
                />
              )}
              {selected.type === "scale" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="أدنى" type="number" value={String(selected.min ?? 1)} onChange={(v) => updateField({ min: Number(v) || 1 })} />
                  <Field label="أعلى" type="number" value={String(selected.max ?? 5)} onChange={(v) => updateField({ max: Number(v) || 5 })} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SortableFieldItem({ f, selected, onSelect, onDelete }: any) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: f.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <li ref={setNodeRef} style={style} className={`flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm ${selected ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:bg-accent/15"}`}>
      <button {...attributes} {...listeners} className="cursor-grab text-muted-foreground/60 hover:text-foreground">
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <button onClick={onSelect} className="flex-1 truncate text-start">
        {f.label_ar} <span className="ms-1 text-[10px] text-muted-foreground/60">{f.type}</span>
      </button>
      <button onClick={onDelete} className="rounded-full p-1 text-muted-foreground/60 hover:bg-destructive/20 hover:text-destructive">
        <Trash2 className="h-3 w-3" />
      </button>
    </li>
  );
}

function Field({ label, value, onChange, textarea, rows = 3, type = "text", mono }: { label: string; value: string; onChange: (v: string) => void; textarea?: boolean; rows?: number; type?: string; mono?: boolean }) {
  const cls = `w-full rounded-xl border border-input bg-background px-3 py-2 outline-none focus:border-ring ${mono ? "font-mono text-sm" : "text-base"}`;
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-muted-foreground">{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} className={cls} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />
      )}
    </label>
  );
}
